const AUTH_SESSION_KEY = 'tourAuthSession';
const EXPIRY_SKEW_MS = 30 * 1000;
let volatileAccessToken = null;
let volatileAccessExpiresAt = null;

function getSessionStorage() {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            return window.sessionStorage;
        }
    } catch (error) {
        return null;
    }
    return null;
}

function parseExpiry(expiresAt) {
    if (!expiresAt) return null;
    const ts = Date.parse(expiresAt);
    return Number.isNaN(ts) ? null : ts;
}

function resolveRelativeExpiry(seconds) {
    if (!seconds) return null;
    const numericSeconds = Number(seconds);
    if (!Number.isFinite(numericSeconds)) return null;
    return new Date(Date.now() + numericSeconds * 1000).toISOString();
}

function isFutureExpiry(expiresAt) {
    const expiryTimestamp = parseExpiry(expiresAt);
    if (!expiryTimestamp) return true;
    return Date.now() + EXPIRY_SKEW_MS < expiryTimestamp;
}

export function getAuthSession() {
    const storage = getSessionStorage();
    if (!storage) return null;
    try {
        const raw = storage.getItem(AUTH_SESSION_KEY);
        if (!raw) return null;
        const session = JSON.parse(raw);

        // Migrate older sessions that persisted access tokens before refresh-token
        // persistence was introduced for reload support.
        if (session?.accessToken) {
            volatileAccessToken = volatileAccessToken || session.accessToken;
            volatileAccessExpiresAt = volatileAccessExpiresAt || session.expiresAt || null;
            delete session.accessToken;
            storage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        }

        return {
            ...session,
            accessToken: volatileAccessToken,
            expiresAt: volatileAccessExpiresAt || session.expiresAt || null,
        };
    } catch (error) {
        return null;
    }
}

export function setAuthSession(session) {
    const storage = getSessionStorage();
    if (!storage) return;

    const currentSession = getAuthSession() || {};
    const nextSession = {
        ...currentSession,
        ...(session || {}),
    };

    if (nextSession.accessToken) {
        volatileAccessToken = nextSession.accessToken;
        volatileAccessExpiresAt = nextSession.expiresAt || null;
    }

    const persistableSession = { ...nextSession };
    delete persistableSession.accessToken;
    storage.setItem(AUTH_SESSION_KEY, JSON.stringify(persistableSession));
}

export function clearAuthSession() {
    const storage = getSessionStorage();
    volatileAccessToken = null;
    volatileAccessExpiresAt = null;
    if (!storage) return;
    storage.removeItem(AUTH_SESSION_KEY);
}

export function getAccessToken() {
    const session = getAuthSession();
    if (!session?.accessToken || !isFutureExpiry(session.expiresAt)) return null;
    return session?.accessToken || null;
}

export function getRefreshToken() {
    const session = getAuthSession();
    if (!session?.refreshToken || !isFutureExpiry(session.refreshExpiresAt)) return null;
    return session.refreshToken;
}

export function getLockedTourId() {
    const session = getAuthSession();
    return session?.tourId || null;
}

export function setLockedTourId(tourId) {
    if (!tourId) return;
    const session = getAuthSession();
    if (!session) return;
    setAuthSession({
        ...session,
        tourId: String(tourId),
    });
}

export function hasValidAccessToken() {
    const session = getAuthSession();
    if (!session?.accessToken) return false;

    return isFutureExpiry(session.expiresAt);
}

export function hasValidRefreshToken() {
    const session = getAuthSession();
    if (!session?.refreshToken) return false;

    return isFutureExpiry(session.refreshExpiresAt);
}

export function hasAuthenticatedSession() {
    return hasValidAccessToken() || hasValidRefreshToken();
}

export function buildAuthSessionFromLogin(responseData, tourId) {
    const accessToken =
        responseData?.access_token ||
        responseData?.accessToken ||
        responseData?.token ||
        null;

    const refreshToken =
        responseData?.refresh_token ||
        responseData?.refreshToken ||
        null;

    let expiresAt = responseData?.expires_at || responseData?.expiresAt || null;
    const expiresIn =
        responseData?.expires_in ||
        responseData?.expiresIn ||
        responseData?.access_token_expires_in ||
        null;

    if (!expiresAt && expiresIn) {
        expiresAt = resolveRelativeExpiry(expiresIn);
    }

    let refreshExpiresAt =
        responseData?.refresh_token_expires_at ||
        responseData?.refreshTokenExpiresAt ||
        responseData?.refresh_expires_at ||
        responseData?.refreshExpiresAt ||
        null;

    const refreshExpiresIn =
        responseData?.refresh_token_expires_in ||
        responseData?.refreshTokenExpiresIn ||
        responseData?.refresh_expires_in ||
        responseData?.refreshExpiresIn ||
        null;

    if (!refreshExpiresAt && refreshExpiresIn) {
        refreshExpiresAt = resolveRelativeExpiry(refreshExpiresIn);
    }

    return {
        accessToken,
        refreshToken,
        expiresAt,
        refreshExpiresAt,
        tourId: tourId ? String(tourId) : null,
    };
}

export function persistLoginSession(responseData, tourId) {
    const storage = getSessionStorage();
    if (!storage) return null;

    const session = buildAuthSessionFromLogin(responseData, tourId);
    if (!session.accessToken || !session.refreshToken) {
        clearAuthSession();
        return null;
    }

    setAuthSession(session);
    return session;
}
